import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { hasEntity } from '@frontend/wetags';
import type { WetagQuery } from '@frontend/wetags';
import { MessagingInboxProp } from './hooks';

function buildGroupMessagingQuery(locationIds: string[]): WetagQuery | null {
  if (!locationIds.length) return null;

  let query: WetagQuery = hasEntity(locationIds[0], 'group-messaging');
  for (let i = 1; i < locationIds.length; i++) {
    query = query.and.hasEntity(locationIds[i], 'group-messaging');
  }
  return query;
}

/**
 * Returns whether group messaging is enabled for ALL GLS-selected location IDs that have
 * inbox (SMS_INBOX_MANAGE) permission — AND logic across all of them.
 *
 * Returns `[false, false]` immediately when no qualifying locations exist.
 */
export function useGroupMessagingEnabled(): [boolean, boolean] {
  const { selectedLocationIds } = useAppScopeStore();
  const locationIdsWithACL = useMemo(
    () =>
      filterLocationIdsByACLs({
        locationIds: selectedLocationIds,
        acls: Permission.SMS_INBOX_MANAGE,
      }),
    [selectedLocationIds]
  );

  const query = useMemo(() => buildGroupMessagingQuery(locationIdsWithACL), [locationIdsWithACL]);

  const [enabled, loading] = MessagingInboxProp.useWetag(
    // When no locations, fall back to the plain tag string (result will be disabled via options)
    query ?? 'group-messaging',
    locationIdsWithACL[0] ?? '',
    { enabled: locationIdsWithACL.length > 0 }
  );

  if (!locationIdsWithACL.length) return [false, false];
  return [enabled, loading];
}
